import type { CopyRecord } from '../types'

declare global {
  interface Window { showDirectoryPicker?: (options?: { mode?: 'read' | 'readwrite' }) => Promise<FileSystemDirectoryHandle> }
}

export function supportsLocalDestination() {
  return typeof window.showDirectoryPicker === 'function'
}

export async function pickDestination() {
  if (!window.showDirectoryPicker) throw new Error('Este navegador no permite elegir una carpeta local. Abre libregoo en Chrome o Edge actualizado.')
  return window.showDirectoryPicker({ mode: 'readwrite' })
}

function safeName(value: string) {
  return value.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '·').replace(/\s+/g, ' ').trim().slice(0, 120) || 'sin-nombre'
}

function numberedName(name: string, copy: number) {
  const dot = name.lastIndexOf('.')
  if (dot <= 0) return `${name} (${copy})`
  return `${name.slice(0, dot)} (${copy})${name.slice(dot)}`
}

async function childDirectory(root: FileSystemDirectoryHandle, segments: string[]) {
  let current = root
  for (const segment of segments) current = await current.getDirectoryHandle(safeName(segment), { create: true })
  return current
}

async function uniqueFileHandle(directory: FileSystemDirectoryHandle, requestedName: string) {
  const base = safeName(requestedName)
  for (let attempt = 0; attempt < 1000; attempt += 1) {
    const candidate = attempt === 0 ? base : numberedName(base, attempt)
    try {
      await directory.getFileHandle(candidate)
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotFoundError') {
        return { name: candidate, handle: await directory.getFileHandle(candidate, { create: true }) }
      }
      throw error
    }
  }
  throw new Error(`No se encontró un nombre libre para «${base}».`)
}

export async function writeBlob(root: FileSystemDirectoryHandle, relativePath: string, blob: Blob) {
  const parts = relativePath.split('/').filter(Boolean)
  const requestedName = parts.pop() ?? 'archivo'
  const directory = await childDirectory(root, parts)
  const { name, handle } = await uniqueFileHandle(directory, requestedName)
  const writable = await handle.createWritable({ keepExistingData: false })
  try {
    await writable.write(blob)
    await writable.close()
  } catch (error) {
    await writable.abort().catch(() => undefined)
    await directory.removeEntry(name).catch(() => undefined)
    throw error
  }
  return [...parts.map(safeName), name].join('/')
}

export async function writeManifest(root: FileSystemDirectoryHandle, records: CopyRecord[], metadata: Record<string, unknown>) {
  const manifest = {
    herramienta: 'libregoo Directo PWA',
    generado: new Date().toISOString(),
    nota: 'Inventario local de esta ejecución. Los hashes completos y la reanudación se incorporarán en la aplicación de escritorio.',
    ...metadata,
    archivos: records,
  }
  return writeBlob(root, 'libregoo/manifiesto-copia-clara.json', new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' }))
}
